import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, Loader2, PlaySquare, RotateCcw } from "lucide-react";
import {
  VERSION_LABELS,
  VERSION_SORT_ORDER,
  type VersionType,
} from "@primetime/shared";
import type {
  CanonicalAssetGroupDto,
  CatalogResponseDto,
  CreateUnlockRequest,
  CreateUnlockResponse,
  UserEntitlementDto,
} from "@primetime/shared/apiContracts";
import {
  api,
  ApiError,
  getAuthToken,
  resolveApiMediaUrl,
  resolveApiUrl,
} from "../services/apiClient";
import { useAuth } from "../features/auth/AuthContext";
import { dispatchAccountStateSync } from "../features/account/accountStateSync";
import { useAccountCredits } from "../features/account/useAccountCredits";
import { getCreditCost } from "../features/access/catalogAccess";

interface TrackVersionDetail {
  trackVersionId: string;
  versionType: VersionType;
  editorName?: string | null;
  availability?: string | null;
  downloadFilename?: string | null;
}

interface TrackDetailDto {
  trackId: string;
  assetGroupId?: string | null;
  title: string;
  artist: string;
  artistDisplay?: string | null;
  genre: string;
  style?: string | null;
  bpm: number;
  date?: string | null;
  artworkUrl?: string | null;
  editorName?: string | null;
  editLabel?: string | null;
  totalDownloads?: number;
  previewVideoUrl?: string | null;
  previewAudioUrl?: string | null;
  versions: TrackVersionDetail[];
}

type PendingUnlock = {
  version: TrackVersionDetail;
  creditCost: number;
  idempotencyKey: string;
  isRenewal: boolean;
};

type DownloadTarget = {
  entitlementId: string;
  version: TrackVersionDetail;
};

function canonicalVersionType(
  variant: CanonicalAssetGroupDto["downloadVersions"][number]["downloadVariant"],
): VersionType {
  if (variant === "Dirty") return "DIRTY";
  if (variant === "DJ Edit") return "DJ_EDIT";
  return "CLEAN";
}

function mapCanonicalTrackDetail(asset: CanonicalAssetGroupDto): TrackDetailDto {
  const releaseDate = Number.isInteger(asset.releaseYear)
    ? `${asset.releaseYear}-01-01`
    : null;
  const cleanPreview = asset.downloadVersions.find(
    (version) => version.downloadVariant === "Clean",
  )?.previewVideoUrl;

  return {
    trackId: asset.assetGroupId,
    assetGroupId: asset.assetGroupId,
    title: asset.title,
    artist: asset.artist,
    artistDisplay: asset.artist,
    genre: asset.genre || "",
    style: asset.subgenre || null,
    bpm: asset.bpm || 0,
    date: releaseDate,
    artworkUrl: asset.artworkUrl || "/img/logo-primetime.png",
    editorName: asset.editorName,
    editLabel: asset.editLabel,
    totalDownloads: 0,
    previewVideoUrl: cleanPreview || null,
    previewAudioUrl: null,
    versions: asset.downloadVersions.map((version) => ({
      trackVersionId: version.trackVersionId,
      versionType: canonicalVersionType(version.downloadVariant),
      editorName: asset.editLabel || asset.editorName,
      availability: version.availability,
      downloadFilename: version.downloadFilename,
    })),
  };
}

function mergeCanonicalTrackDetail(
  legacy: TrackDetailDto,
  asset: CanonicalAssetGroupDto,
): TrackDetailDto {
  const canonical = mapCanonicalTrackDetail(asset);
  return {
    ...legacy,
    ...canonical,
    totalDownloads: legacy.totalDownloads,
  };
}

function indexTrackEntitlements(entitlements: UserEntitlementDto[]) {
  const indexed = new Map<string, UserEntitlementDto>();
  for (const entitlement of entitlements) {
    if (entitlement.productType !== "TRACK_VERSION") continue;
    const trackVersionId = entitlement.trackVersionId || entitlement.productId;
    if (trackVersionId) indexed.set(trackVersionId, entitlement);
  }
  return indexed;
}

export function TrackDetail() {
  const { trackId } = useParams();
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();
  const {
    creditState,
    creditStateReady,
    markInsufficientCredits,
    requiresSubscribe
  } = useAccountCredits(user?.id, authLoading);
  const inFlightActionRef = useRef<string | null>(null);
  const [track, setTrack] = useState<TrackDetailDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [entitlementsByVersionId, setEntitlementsByVersionId] = useState<
    Map<string, UserEntitlementDto>
  >(() => new Map());
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingUnlock, setPendingUnlock] = useState<PendingUnlock | null>(null);
  const [downloadRetry, setDownloadRetry] = useState<DownloadTarget | null>(null);

  useEffect(() => {
    if (!trackId) return;
    let active = true;
    setIsLoading(true);
    setError("");

    const legacyRequest = api<TrackDetailDto>(`/api/tracks/${trackId}`);
    const canonicalRequest = api<CatalogResponseDto>("/api/catalog");

    Promise.allSettled([legacyRequest, canonicalRequest])
      .then(([legacyResult, canonicalResult]) => {
        if (!active) return;

        const legacy =
          legacyResult.status === "fulfilled" ? legacyResult.value : null;
        const canonicalAsset =
          canonicalResult.status === "fulfilled"
            ? canonicalResult.value.assetGroups.find(
                (asset) => asset.assetGroupId === trackId,
              ) || null
            : null;

        if (canonicalAsset) {
          setTrack(
            legacy
              ? mergeCanonicalTrackDetail(legacy, canonicalAsset)
              : mapCanonicalTrackDetail(canonicalAsset),
          );
          return;
        }

        if (legacy) {
          setTrack(legacy);
          return;
        }

        const err = legacyResult.status === "rejected" ? legacyResult.reason : null;
        setTrack(null);
        setError(
          err instanceof ApiError && err.status === 404
            ? "Track not found."
            : "Unable to load this track.",
        );
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [trackId]);

  const refreshEntitlements = useCallback(async () => {
    if (!user) {
      const empty = new Map<string, UserEntitlementDto>();
      setEntitlementsByVersionId(empty);
      return empty;
    }

    try {
      const entitlements = await api<UserEntitlementDto[]>(
        "/api/account/entitlements",
      );
      const indexed = indexTrackEntitlements(entitlements);
      setEntitlementsByVersionId(indexed);
      return indexed;
    } catch {
      const empty = new Map<string, UserEntitlementDto>();
      setEntitlementsByVersionId(empty);
      return null;
    }
  }, [user]);

  useEffect(() => {
    void refreshEntitlements();
  }, [refreshEntitlements]);

  function isPublishedVersion(version?: TrackVersionDetail | null) {
    return Boolean(
      track?.assetGroupId &&
        version?.availability?.trim().toLowerCase() === "available",
    );
  }

  function getActionErrorMessage(error: unknown, fallback: string) {
    if (!(error instanceof ApiError)) return fallback;
    if (error.status === 401) return "Your session has expired. Please log in again.";
    if (error.status === 402) {
      return "Not enough PrimeTime Credits are available for this unlock.";
    }
    if (error.status === 403) {
      return error.message || "Active Member Access is required for this action.";
    }
    if ([404, 409, 422, 503].includes(error.status)) {
      return error.message || "This track version is not currently available.";
    }
    return error.message || fallback;
  }

  async function downloadAuthorizedVersion(
    entitlementId: string,
    version: TrackVersionDetail,
  ) {
    const token = getAuthToken();
    const response = await fetch(
      resolveApiUrl(`/api/downloads/${encodeURIComponent(entitlementId)}`),
      {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    );

    if (!response.ok) {
      let message = response.statusText;
      try {
        const body = (await response.json()) as { error?: string };
        message = body.error || message;
      } catch {
        // Preserve the HTTP status when the secure download response is not JSON.
      }
      throw new ApiError(response.status, message);
    }

    const contentType = response.headers.get("content-type")?.toLowerCase() || "";
    if (contentType.includes("application/json")) {
      const result = (await response.json()) as { url?: string };
      if (!result.url) throw new ApiError(502, "Unable to start secure download.");
      const anchor = document.createElement("a");
      anchor.href = result.url;
      anchor.download = version.downloadFilename || `${version.trackVersionId}.mp4`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return;
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = version.downloadFilename || `${version.trackVersionId}.mp4`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  function markDownloadCounted(versionId: string) {
    setEntitlementsByVersionId((current) => {
      const entitlement = current.get(versionId);
      if (!entitlement) return current;
      const next = new Map(current);
      const downloadsUsed = entitlement.downloadsUsed + 1;
      const remainingDownloads = Math.max(entitlement.remainingDownloads - 1, 0);
      next.set(versionId, {
        ...entitlement,
        downloadsUsed,
        remainingDownloads,
        status: remainingDownloads > 0 ? "Available" : "Locked",
      });
      return next;
    });
  }

  async function completeAuthorizedDownload(target: DownloadTarget) {
    await downloadAuthorizedVersion(target.entitlementId, target.version);
    markDownloadCounted(target.version.trackVersionId);
    dispatchAccountStateSync({ refreshLibrary: true });
  }

  async function startAuthorizedDownload(target: DownloadTarget) {
    if (inFlightActionRef.current) return;

    const actionKey = `${target.version.trackVersionId}:download`;
    inFlightActionRef.current = actionKey;
    setActiveActionKey(actionKey);
    setActionError(null);
    try {
      await completeAuthorizedDownload(target);
      setDownloadRetry(null);
    } catch (error) {
      const isDownloadLimitError =
        error instanceof ApiError &&
        error.status === 403 &&
        error.message.toLowerCase().includes("limit");
      let retryTarget: DownloadTarget | null = isDownloadLimitError ? null : target;

      if (error instanceof ApiError && error.status === 404) {
        const refreshed = await refreshEntitlements();
        if (refreshed && !refreshed.has(target.version.trackVersionId)) {
          retryTarget = null;
        }
      }

      setDownloadRetry(retryTarget);
      setActionError(
        getActionErrorMessage(error, "Unable to start secure download."),
      );
    } finally {
      if (inFlightActionRef.current === actionKey) {
        inFlightActionRef.current = null;
        setActiveActionKey(null);
      }
    }
  }

  function queueVersionUnlock(version: TrackVersionDetail, isRenewal = false) {
    if (!track || !isPublishedVersion(version)) return;
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    const creditCost = getCreditCost(version.versionType);
    if (!creditStateReady) return;
    if (requiresSubscribe(version.trackVersionId, creditCost)) {
      navigate("/pricing");
      return;
    }
    if (inFlightActionRef.current || pendingUnlock) return;

    setActionError(null);
    setDownloadRetry(null);
    setPendingUnlock({
      version,
      creditCost,
      idempotencyKey: `track-detail-${isRenewal ? "renewal" : "unlock"}-${track.assetGroupId}-${version.trackVersionId}-${crypto.randomUUID()}`,
      isRenewal,
    });
  }

  async function confirmPendingUnlock() {
    const target = pendingUnlock;
    if (
      !target ||
      authLoading ||
      !user ||
      !track?.assetGroupId ||
      !creditStateReady ||
      inFlightActionRef.current
    ) {
      return;
    }

    const unlockActionKey = `${target.version.trackVersionId}:unlock`;
    inFlightActionRef.current = unlockActionKey;
    setActiveActionKey(unlockActionKey);
    setActionError(null);

    try {
      const request: CreateUnlockRequest = {
        productType: "TRACK_VERSION",
        assetGroupId: track.assetGroupId,
        trackVersionId: target.version.trackVersionId,
        idempotencyKey: target.idempotencyKey,
      };
      const result = await api<CreateUnlockResponse>("/api/unlocks", {
        method: "POST",
        body: JSON.stringify(request),
      });

      dispatchAccountStateSync({
        updatedCredits: result.updatedCredits,
        refreshLibrary: true,
      });
      setPendingUnlock(null);
      setDownloadRetry(null);
      await refreshEntitlements();

      const downloadTarget: DownloadTarget = {
        entitlementId: result.entitlement.entitlementId,
        version: target.version,
      };
      const downloadActionKey = `${downloadTarget.version.trackVersionId}:download`;
      inFlightActionRef.current = downloadActionKey;
      setActiveActionKey(downloadActionKey);

      try {
        await completeAuthorizedDownload(downloadTarget);
      } catch (error) {
        setDownloadRetry(downloadTarget);
        setActionError(
          getActionErrorMessage(
            error,
            "Unlock succeeded, but the secure download could not start.",
          ),
        );
      } finally {
        if (inFlightActionRef.current === downloadActionKey) {
          inFlightActionRef.current = null;
          setActiveActionKey(null);
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 402) {
        await markInsufficientCredits(target.version.trackVersionId, target.creditCost);
        await refreshAuth();
      }
      setActionError(getActionErrorMessage(error, "Unable to unlock this version."));
    } finally {
      if (inFlightActionRef.current === unlockActionKey) {
        inFlightActionRef.current = null;
        setActiveActionKey(null);
      }
    }
  }

  function handleVersionDownload(version: TrackVersionDetail) {
    if (!isPublishedVersion(version) || inFlightActionRef.current) return;

    const entitlement = entitlementsByVersionId.get(version.trackVersionId);
    if (!entitlement) {
      if (authLoading) return;
      const creditCost = getCreditCost(version.versionType);
      if (!creditStateReady) return;
      if (requiresSubscribe(version.trackVersionId, creditCost)) {
        navigate("/pricing");
        return;
      }
      queueVersionUnlock(version);
      return;
    }
    if (entitlement.remainingDownloads <= 0) {
      if (authLoading) return;
      const creditCost = getCreditCost(version.versionType);
      if (!creditStateReady) return;
      if (requiresSubscribe(version.trackVersionId, creditCost)) {
        navigate("/pricing");
        return;
      }
      queueVersionUnlock(version, true);
      return;
    }

    void startAuthorizedDownload({
      entitlementId: entitlement.entitlementId,
      version,
    });
  }

  if (isLoading) {
    return (
      <section className="container-shell py-16">
        <div className="glass rounded-3xl p-8 text-white/55">
          Loading track...
        </div>
      </section>
    );
  }

  if (error || !track) {
    return (
      <section className="container-shell py-16">
        <div className="glass rounded-3xl p-8">
          <p className="text-white/70">{error || "Track not found."}</p>
          <Link to="/crates" className="btn-primary mt-6 inline-flex">
            Browse Crates
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="container-shell py-14">
      <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="glass-red overflow-hidden rounded-3xl p-5 flex flex-col gap-4">
          <div className="flex aspect-video items-center justify-center rounded-2xl bg-black overflow-hidden relative">
            {track.previewVideoUrl ? (
              <video
                src={resolveApiMediaUrl(track.previewVideoUrl) || undefined}
                controls
                controlsList="nodownload"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center p-6 text-center">
                <img
                  src={track.artworkUrl || "/img/logo-primetime.png"}
                  alt=""
                  className="h-full w-full object-contain opacity-30 blur-[2px] transition-all"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
                  <PlaySquare size={48} className="mb-3 text-white/40" />
                  <p className="text-sm font-bold uppercase tracking-widest text-white/70">
                    Preview Coming Soon
                  </p>
                </div>
              </div>
            )}
          </div>

          {track.previewAudioUrl && !track.previewVideoUrl && (
            <audio
              src={resolveApiMediaUrl(track.previewAudioUrl) || undefined}
              controls
              controlsList="nodownload"
              className="w-full"
            />
          )}

          <div className="rounded-xl border border-prime-red/20 bg-prime-red/5 p-3 text-center">
            <p className="text-xs text-white/50">
              Low quality preview snippet only. Full HQ download protected.
            </p>
          </div>
        </div>

        <div className="glass rounded-3xl p-8">
          <p className="text-xs font-black uppercase tracking-[0.28em] text-prime-red">
            Track Detail
          </p>
          <h1 className="mt-4 text-4xl font-black tracking-tight">
            {track.title}
          </h1>
          <p className="mt-2 text-xl text-white/60">{track.artistDisplay || track.artist}</p>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-white/35">
                Genre
              </p>
              <p className="mt-1 font-bold text-white">{track.genre}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-white/35">
                BPM
              </p>
              <p className="mt-1 font-bold text-white">{track.bpm}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-white/35">
                Downloads
              </p>
              <p className="mt-1 font-bold text-white">
                {track.totalDownloads || 0}
              </p>
            </div>
          </div>

          {track.style && (
            <p className="mt-5 text-sm font-bold uppercase tracking-wider text-white/45">
              {track.style}
            </p>
          )}

          {actionError && (
            <div
              role="alert"
              className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-prime-red/30 bg-prime-red/10 p-4 text-sm font-bold text-red-100"
            >
              <span>{actionError}</span>
              {downloadRetry && (
                <button
                  type="button"
                  onClick={() => void startAuthorizedDownload(downloadRetry)}
                  disabled={Boolean(activeActionKey)}
                  className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-xs font-black text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RotateCcw size={13} /> Retry Download
                </button>
              )}
            </div>
          )}

          <div className="mt-8">
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-white/70">
              Available Versions
            </h2>
            {track.versions.length === 0 ? (
              <p className="mt-4 rounded-xl border border-white/10 bg-white/[0.04] p-4 text-sm text-white/50">
                No active versions are available.
              </p>
            ) : (
              <div className="mt-4 flex flex-wrap gap-3">
                {(() => {
                  const sorted = [...track.versions].sort(
                    (a, b) =>
                      (VERSION_SORT_ORDER[a.versionType] || 99) -
                      (VERSION_SORT_ORDER[b.versionType] || 99),
                  );
                  const clean = sorted.find((v) => v.versionType === "CLEAN");
                  const dirty = sorted.find((v) => v.versionType === "DIRTY");
                  const others = sorted.filter(
                    (v) =>
                      v.versionType !== "CLEAN" && v.versionType !== "DIRTY",
                  );
                  const orderedVersions = [clean, dirty, ...others].filter(
                    (version): version is TrackVersionDetail => Boolean(version),
                  );
                  const lockedStates = orderedVersions.map((version) => {
                    const entitlement = entitlementsByVersionId.get(version.trackVersionId);
                    const isUnlocked = Boolean(entitlement);
                    const exhausted = Boolean(
                      entitlement && entitlement.remainingDownloads <= 0,
                    );
                    const needsPaidAction = !isUnlocked || exhausted;
                    const creditStatePending =
                      needsPaidAction && (authLoading || creditState.status === 'loading');
                    return {
                      version,
                      needsSubscribe:
                        needsPaidAction &&
                        isPublishedVersion(version) &&
                        requiresSubscribe(version.trackVersionId, getCreditCost(version.versionType)),
                      creditStatePending,
                      creditStateError:
                        needsPaidAction &&
                        isPublishedVersion(version) &&
                        Boolean(user) &&
                        creditState.status === 'error',
                    };
                  });
                  const showSubscribe = lockedStates.some(
                    (state) => state.needsSubscribe || state.creditStateError,
                  );

                  const renderPill = (v: TrackVersionDetail) => {
                    const entitlement = entitlementsByVersionId.get(
                      v.trackVersionId,
                    );
                    const isUnlocked = Boolean(entitlement);
                    const creditCost = getCreditCost(v.versionType);
                    const isBusy =
                      activeActionKey === `${v.trackVersionId}:download` ||
                      activeActionKey === `${v.trackVersionId}:unlock`;
                    const unavailable = !isPublishedVersion(v);
                    const exhausted = Boolean(
                      entitlement && entitlement.remainingDownloads <= 0,
                    );
                    const needsPaidAction = !isUnlocked || exhausted;
                    const needsSubscribe =
                      needsPaidAction && requiresSubscribe(v.trackVersionId, creditCost);
                    const creditStatePending =
                      needsPaidAction && (authLoading || creditState.status === 'loading');
                    if (
                      needsPaidAction &&
                      isPublishedVersion(v) &&
                      (needsSubscribe || creditStatePending || (Boolean(user) && creditState.status === 'error'))
                    ) {
                      return null;
                    }
                    const disabled =
                      unavailable ||
                      Boolean(activeActionKey);
                    const actionLabel = unavailable
                      ? "Unavailable"
                      : exhausted
                        ? `Renew ${creditCost} ${creditCost === 1 ? "Credit" : "Credits"}`
                        : isBusy
                          ? "Preparing..."
                          : isUnlocked
                            ? "Download"
                            : `Unlock ${creditCost} ${creditCost === 1 ? "Credit" : "Credits"}`;
                    return (
                      <button
                        type="button"
                        key={v.trackVersionId}
                        onClick={() => void handleVersionDownload(v)}
                        disabled={disabled}
                        aria-busy={isBusy}
                        aria-label={`${actionLabel} ${VERSION_LABELS[v.versionType as VersionType]} version`}
                        className={`version-pill-priced flex items-center gap-2 px-4 py-2 text-sm font-bold transition-all ${
                          isUnlocked && !exhausted
                            ? "bg-green-600 text-white border-green-500 shadow-none"
                            : disabled
                              ? "cursor-not-allowed opacity-50"
                              : "hover:bg-prime-red hover:text-white hover:border-prime-red"
                        }`}
                      >
                        <span>
                          {VERSION_LABELS[v.versionType as VersionType]}
                        </span>
                        {isBusy && <Loader2 size={14} className="animate-spin" />}
                        {!isBusy && isUnlocked && !exhausted && <Download size={14} />}
                        <span>{actionLabel}</span>
                      </button>
                    );
                  };

                  return (
                    <div className="flex flex-wrap gap-3">
                      {clean && renderPill(clean)}
                      {dirty && renderPill(dirty)}
                      {others.map(renderPill)}
                      {showSubscribe && (
                        <button
                          type="button"
                          onClick={() => navigate("/pricing")}
                          className="version-pill-priced btn-primary flex items-center gap-2 px-4 py-2 text-sm font-bold"
                          aria-label="Subscribe for PrimeTime Credits"
                        >
                          SUBSCRIBE
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {pendingUnlock && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="track-detail-unlock-title"
          aria-describedby="track-detail-unlock-description"
        >
          <div className="glass w-full max-w-[460px] rounded-2xl border border-white/10 p-5 shadow-2xl">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-prime-red">PRIMETIME CREDITS</p>
            <h2 id="track-detail-unlock-title" className="mt-2 text-xl font-black text-white">GET 5 DOWNLOADS</h2>
            <div className="mt-3 min-w-0">
              <p className="break-words text-[15px] font-black leading-5 text-white">{track?.title}</p>
              <p className="mt-1 break-words text-[12px] leading-5 text-white/60">
                {track?.artistDisplay || track?.artist}
                <span className="mx-2 text-white/30">•</span>
                <span className="font-black uppercase text-prime-red">
                  {track?.editLabel?.trim() || track?.editorName?.trim() || pendingUnlock.version.editorName?.trim() || VERSION_LABELS[pendingUnlock.version.versionType]}
                </span>
              </p>
            </div>
            <div className="mt-3 rounded-xl border border-white/15 bg-white/[0.04] p-3 text-xs">
              <div className="flex items-center justify-between gap-4">
                <span className="text-white/55">Version</span>
                <span className="font-black text-white">{VERSION_LABELS[pendingUnlock.version.versionType]}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2">
                <span className="text-white/55">Cost</span>
                <span className="font-black text-white">{pendingUnlock.creditCost} PrimeTime {pendingUnlock.creditCost === 1 ? "Credit" : "Credits"}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2">
                <span className="text-white/55">Downloads included</span>
                <span className="font-black text-white">5</span>
              </div>
            </div>
            <p id="track-detail-unlock-description" className="mt-3 text-[12px] leading-5 text-white/60">
              Your MP4 will download automatically after confirmation. Your remaining 4 downloads will be available in My Library.
            </p>

            {actionError && (
              <p role="alert" className="mt-4 text-xs font-bold text-red-200">
                {actionError}
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (activeActionKey) return;
                  setPendingUnlock(null);
                  setActionError(null);
                }}
                disabled={Boolean(activeActionKey)}
                    className="w-full rounded-lg border border-white/15 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-white/70 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmPendingUnlock()}
                disabled={Boolean(activeActionKey)}
                    className="btn-primary inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-xs font-black uppercase tracking-wider disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {activeActionKey ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : null}
                {activeActionKey
                  ? pendingUnlock.isRenewal ? "Renewing..." : "Unlocking..."
                  : "Confirm & Download"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
